import * as React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { isString } from '@agilepoint/utils-common';
import { IFlyoutFooterProps, FlyoutFooter } from './flyout-footer';
import { IFlyoutHeaderProps, FlyoutHeader } from './flyout-header';
import { position, IFlyoutProps } from '../flyout-props';
import { selectThemeSemantics } from '../../../styling';
import { useChooseRef } from '../../../utils';

const flyoutSize: { small: number; medium: number; large: number } = {
    small: 300,
    medium: 664,
    large: 1000
};

const ContentWrapper = styled.div.attrs({
    className: String('ap-flyout-content')
})`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    overflow-y: auto;
    padding: ${selectThemeSemantics(({ gap }) => gap.none)};
`;

const slideOutFromLeft = keyframes`
    from { transform: translateX(0%); }
    to { transform: translateX(-100%); }
`;

const slideOutFromRight = keyframes`
    from { transform: translateX(0%); }
    to { transform: translateX(100%); }
`;

const slideInFromRight = keyframes`
    from { transform: translateX(100%); }
    to { transform: translateX(0%); }
`;

const slideInFromLeft = keyframes`
    from { transform: translateX(-100%); }
    to { transform: translateX(0%); }
`;

function getAnimationStyle(flyoutPosition?: position, isClosing?: boolean) {
    if (isClosing) {
        return flyoutPosition === 'left' ? slideOutFromLeft : slideOutFromRight;
    }
    return flyoutPosition === 'left' ? slideInFromLeft : slideInFromRight;
}

interface IFlyoutContainerProps extends Required<Pick<IFlyoutProps, 'size' | 'decorator' | 'decoratorProps'>>, IFlyoutFooterProps, IFlyoutHeaderProps {
    animationDuration: number;
    children?: React.ReactNode;
    className?: string;
    flyoutPosition: position;
    isClosing?: boolean;
    elementRef: React.RefObject<HTMLDivElement>;
    width?: number;
    onRenderFooterContent?(): React.ReactNode;
}

const FlyoutContainerBase = React.memo((props: IFlyoutContainerProps) => {
    const { className, children, title, showTitleSeparator, onCloseFlyout, onRenderFooterContent, footerAlignment, elementRef, decorator, decoratorProps } = props;
    const rootElementRef = useChooseRef(elementRef) as React.RefObject<HTMLDivElement>;
    const wrapperProps = React.useMemo(() => (
            isString(decorator)
                ? { className, ref: rootElementRef, tabIndex: 0, ...decoratorProps }
                : { className, elementRef: rootElementRef, tabIndex: 0, ...decoratorProps }
        ), [className, decorator, decoratorProps, rootElementRef]);

        React.useEffect(() => {
            if(rootElementRef.current) {
                rootElementRef.current.focus();
            }
        });

    return React.createElement(decorator, wrapperProps, (
        <React.Fragment>
            <FlyoutHeader showTitleSeparator={showTitleSeparator} title={title} onCloseFlyout={onCloseFlyout} />
            <ContentWrapper>
                {children}
            </ContentWrapper>
            <FlyoutFooter onRenderFooterContent={onRenderFooterContent} footerAlignment={footerAlignment} />
        </React.Fragment>
    ));
});

FlyoutContainerBase.displayName = 'FlyoutContainer';

export const FlyoutContainer = styled(FlyoutContainerBase).attrs(() => ({
    className: String('ap-flyout')
}))`
    display: grid;
    transition: 100ms width ease-in;
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 5;
    max-width: 100%;
    height: 100%; // Setting height property is necessary for Safari browser to allocate the space specified in grid template
    box-sizing: border-box;
    box-shadow: 0 0 20px rgba(0,0,0,0.2);
    visibility: visible;
    grid-template: ${({ onRenderFooterContent }) => `auto minmax(0,1fr) ${onRenderFooterContent !== undefined ? 'auto' : ''}/minmax(0,1fr)`};
    left: ${({ flyoutPosition }) => (flyoutPosition === 'left' ? 0 : 'unset')};
    right: ${({ flyoutPosition }) => (flyoutPosition === 'right' ? 0 : 'unset')};
    border-right: ${({ flyoutPosition, theme: { flyout: { colors }, semantics: { stroke } } }) => (flyoutPosition === 'left' ? `${stroke.thin} solid ${colors.borderColor}` : 'none')};
    border-left: ${({ flyoutPosition, theme: { flyout: { colors }, semantics: { stroke } } }) => (flyoutPosition === 'right' ? `${stroke.thin} solid ${colors.borderColor}` : 'none')};
    width: ${({ size }) => flyoutSize[size]}px;
    background-color: ${({ theme: { flyout: { colors } } }) => colors.backgroundColor};
    ${({ animationDuration, flyoutPosition, isClosing }) =>
        css`animation: ${animationDuration}ms ${getAnimationStyle(flyoutPosition, isClosing)} ease-out;`};
    &:focus-visible {
        outline: none;
    }
`;
